import React, { useState, useEffect } from 'react';
import api, { getImageUrl } from '../../services/api';

interface MediaFile {
  id: number;
  filename: string;
  original_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  created_at: string;
}

interface GalleryItem {
  id: number;
  image_gallery_id: number;
  image_url: string;
  thumbnail_url?: string;
  title?: string;
  description?: string;
  alt_text?: string;
  order: number;
  created_at: string;
  updated_at: string;
}

interface ImageGallery {
  id: number;
  title: string;
  description?: string;
  cover_image?: string;
  category?: string;
  is_active: boolean;
  order: number;
  items?: GalleryItem[];
  created_at: string;
  updated_at: string;
}

const ImageGalleryManager: React.FC = () => {
  const [galleries, setGalleries] = useState<ImageGallery[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGalleryForm, setShowGalleryForm] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingGallery, setEditingGallery] = useState<ImageGallery | null>(null);
  const [selectedGallery, setSelectedGallery] = useState<ImageGallery | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [mediaFilter, setMediaFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');

  const [galleryForm, setGalleryForm] = useState({
    title: '',
    description: '',
    cover_image: '',
    category: '',
    is_active: true,
    order: 0
  });

  const [itemForm, setItemForm] = useState({
    image_url: '',
    thumbnail_url: '',
    title: '',
    description: '',
    alt_text: '',
    order: 0
  });

  useEffect(() => {
    fetchGalleries();
    fetchMediaFiles();
  }, []);

  const fetchGalleries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cms/image-galleries');
      setGalleries(response.data);
    } catch (error: any) {
      console.error('Error fetching galleries:', error);
      alert('Error loading galleries: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMediaFiles = async () => {
    try {
      const response = await api.get('/cms/media');
      setMediaFiles(response.data);
    } catch (error) {
      console.error('Error fetching media files:', error);
    }
  };

  const handleGallerySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingGallery) {
        await api.put(`/cms/image-galleries/${editingGallery.id}`, galleryForm);
      } else {
        await api.post('/cms/image-galleries', galleryForm);
      }
      setShowGalleryForm(false);
      setEditingGallery(null);
      setGalleryForm({
        title: '',
        description: '',
        cover_image: '',
        category: '',
        is_active: true,
        order: 0
      });
      fetchGalleries();
    } catch (error: any) {
      console.error('Error saving gallery:', error);
      alert('Error saving gallery: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    }
  };

  const handleItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGallery) return;

    try {
      await api.post(`/cms/image-galleries/${selectedGallery.id}/items`, itemForm);
      setShowItemForm(false);
      setItemForm({
        image_url: '',
        thumbnail_url: '',
        title: '',
        description: '',
        alt_text: '',
        order: 0
      });
      fetchGalleries();
      if (selectedGallery) {
        const updated = galleries.find(g => g.id === selectedGallery.id);
        if (updated) setSelectedGallery(updated);
      }
    } catch (error: any) {
      console.error('Error adding item:', error);
      alert('Error adding image: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    }
  };

  const handleEditGallery = (gallery: ImageGallery) => {
    setEditingGallery(gallery);
    setGalleryForm({
      title: gallery.title,
      description: gallery.description || '',
      cover_image: gallery.cover_image || '',
      category: gallery.category || '',
      is_active: gallery.is_active,
      order: gallery.order
    });
    setShowGalleryForm(true);
  };

  const handleDeleteGallery = async (galleryId: number) => {
    if (window.confirm('Are you sure you want to delete this gallery? All images will be deleted.')) {
      try {
        await api.delete(`/cms/image-galleries/${galleryId}`);
        fetchGalleries();
        if (selectedGallery?.id === galleryId) {
          setSelectedGallery(null);
          setViewMode('list');
        }
      } catch (error: any) {
        console.error('Error deleting gallery:', error);
        alert('Error deleting gallery: ' + (error.response?.data?.message || error.message || 'Unknown error'));
      }
    }
  };

  const handleDeleteItem = async (galleryId: number, itemId: number) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await api.delete(`/cms/image-galleries/${galleryId}/items/${itemId}`);
        fetchGalleries();
        if (selectedGallery) {
          const updated = galleries.find(g => g.id === selectedGallery.id);
          if (updated) setSelectedGallery(updated);
        }
      } catch (error: any) {
        console.error('Error deleting item:', error);
        alert('Error deleting image: ' + (error.response?.data?.message || error.message || 'Unknown error'));
      }
    }
  };

  const openMediaLibrary = () => {
    setShowMediaLibrary(true);
  };

  const closeMediaLibrary = () => {
    setShowMediaLibrary(false);
    setSelectedMedia(null);
  };

  const selectMedia = (media: MediaFile) => {
    setSelectedMedia(media);
  };

  const insertSelectedMedia = () => {
    if (selectedMedia) {
      const imageUrl = getImageUrl(selectedMedia.file_path);
      setItemForm({ ...itemForm, image_url: imageUrl });
      closeMediaLibrary();
    }
  };

  const filteredMediaFiles = mediaFiles.filter(media => {
    if (mediaFilter === 'all') return true;
    if (mediaFilter === 'images') return media.file_type.startsWith('image/');
    return true;
  });

  const viewGallery = (gallery: ImageGallery) => {
    setSelectedGallery(gallery);
    setViewMode('detail');
  };

  const toggleActive = async (gallery: ImageGallery) => {
    try {
      await api.put(`/cms/image-galleries/${gallery.id}`, {
        ...gallery,
        is_active: !gallery.is_active
      });
      fetchGalleries();
    } catch (error: any) {
      console.error('Error updating gallery:', error);
      alert('Error updating gallery: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    }
  };

  if (loading) {
    return <div className="loading">Loading galleries...</div>;
  }

  if (viewMode === 'detail' && selectedGallery) {
    const gallery = galleries.find(g => g.id === selectedGallery.id) || selectedGallery;
    return (
      <div className="cms-container">
        <div className="cms-header">
          <button className="btn btn-secondary" onClick={() => { setViewMode('list'); setSelectedGallery(null); }}>
            <i className="fas fa-arrow-left"></i> Back to Galleries
          </button>
          <h2>{gallery.title}</h2>
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setItemForm({
                image_url: '',
                thumbnail_url: '',
                title: '',
                description: '',
                alt_text: '',
                order: (gallery.items?.length || 0)
              });
              setShowItemForm(true);
            }}
          >
            <i className="fas fa-plus"></i> Add Image
          </button>
        </div>

        {gallery.description && (
          <p style={{ marginBottom: '20px', color: '#666' }}>{gallery.description}</p>
        )}

        {showItemForm && (
          <div className="modal-overlay" onClick={() => setShowItemForm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
              <div className="modal-header">
                <h3>Add Image to Gallery</h3>
                <button className="close-btn" onClick={() => setShowItemForm(false)}>&times;</button>
              </div>
              <form onSubmit={handleItemSubmit}>
                <div className="form-group">
                  <label>Image URL *</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="url"
                      value={itemForm.image_url}
                      onChange={(e) => setItemForm({ ...itemForm, image_url: e.target.value })}
                      required
                      placeholder="https://example.com/image.jpg or select from media library"
                      style={{ flex: 1 }}
                    />
                    <button type="button" className="btn btn-secondary" onClick={openMediaLibrary}>
                      <i className="fas fa-images"></i> Media Library
                    </button>
                  </div>
                </div>

                {itemForm.image_url && (
                  <div style={{ marginTop: '10px', marginBottom: '20px' }}>
                    <img 
                      src={itemForm.image_url} 
                      alt="Preview" 
                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Thumbnail URL (Optional)</label>
                  <input
                    type="url"
                    value={itemForm.thumbnail_url}
                    onChange={(e) => setItemForm({ ...itemForm, thumbnail_url: e.target.value })}
                    placeholder="https://example.com/thumbnail.jpg"
                  />
                </div>

                <div className="form-group">
                  <label>Title (Optional)</label>
                  <input
                    type="text"
                    value={itemForm.title}
                    onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                    placeholder="Image title"
                  />
                </div>

                <div className="form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    value={itemForm.description}
                    onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                    rows={3}
                    placeholder="Image description"
                  />
                </div>

                <div className="form-group">
                  <label>Alt Text (Optional)</label>
                  <input
                    type="text"
                    value={itemForm.alt_text}
                    onChange={(e) => setItemForm({ ...itemForm, alt_text: e.target.value })}
                    placeholder="Alternative text for accessibility"
                  />
                </div>

                <div className="form-group">
                  <label>Order</label>
                  <input
                    type="number"
                    value={itemForm.order}
                    onChange={(e) => setItemForm({ ...itemForm, order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowItemForm(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Add Image
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {gallery.items && gallery.items.length > 0 ? (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
            gap: '20px',
            marginTop: '20px'
          }}>
            {gallery.items.map((item) => (
              <div key={item.id} style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <div style={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden', background: '#f0f0f0' }}>
                  <img 
                    src={item.thumbnail_url || item.image_url} 
                    alt={item.alt_text || item.title || 'Gallery image'}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = item.image_url;
                    }}
                  />
                </div>
                <div style={{ padding: '15px' }}>
                  {item.title && <h4 style={{ margin: '0 0 5px 0', fontSize: '14px' }}>{item.title}</h4>}
                  <div style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteItem(gallery.id, item.id)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <i className="fas fa-images" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}></i>
            <p>No images in this gallery yet. Add your first image!</p>
          </div>
        )}

        {showMediaLibrary && (
          <div className="modal-overlay" onClick={closeMediaLibrary}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '80vh', overflow: 'auto' }}>
              <div className="modal-header">
                <h3>Media Library</h3>
                <button className="close-btn" onClick={closeMediaLibrary}>&times;</button>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <select value={mediaFilter} onChange={(e) => setMediaFilter(e.target.value)}>
                  <option value="all">All Files</option>
                  <option value="images">Images Only</option>
                </select>
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
                gap: '15px' 
              }}>
                {filteredMediaFiles.map((media) => (
                  <div
                    key={media.id}
                    onClick={() => selectMedia(media)}
                    style={{
                      border: selectedMedia?.id === media.id ? '3px solid #007bff' : '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '10px',
                      cursor: 'pointer',
                      background: selectedMedia?.id === media.id ? '#e7f3ff' : '#fff'
                    }}
                  >
                    {media.file_type.startsWith('image/') ? (
                      <img 
                        src={getImageUrl(media.file_path)} 
                        alt={media.original_name}
                        style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <i className="fas fa-file" style={{ fontSize: '32px', color: '#999' }}></i>
                      </div>
                    )}
                    <p style={{ margin: '5px 0 0 0', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {media.original_name}
                    </p>
                  </div>
                ))}
              </div>
              <div className="form-actions" style={{ marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={closeMediaLibrary}>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={insertSelectedMedia}
                  disabled={!selectedMedia}
                >
                  Use Selected
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="cms-container">
      <div className="cms-header">
        <h2>Image Gallery Management</h2>
        <button 
          className="btn btn-primary" 
          onClick={() => {
            setEditingGallery(null);
            setGalleryForm({
              title: '',
              description: '',
              cover_image: '',
              category: '',
              is_active: true,
              order: 0
            });
            setShowGalleryForm(true);
          }}
        >
          <i className="fas fa-plus"></i> Create New Gallery
        </button>
      </div>

      {showGalleryForm && (
        <div className="modal-overlay" onClick={() => setShowGalleryForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3>{editingGallery ? 'Edit Gallery' : 'Create New Gallery'}</h3>
              <button className="close-btn" onClick={() => setShowGalleryForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleGallerySubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={galleryForm.title}
                  onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                  required
                  placeholder="Gallery title"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={galleryForm.description}
                  onChange={(e) => setGalleryForm({ ...galleryForm, description: e.target.value })}
                  rows={4}
                  placeholder="Gallery description"
                />
              </div>

              <div className="form-group">
                <label>Cover Image URL</label>
                <input
                  type="url"
                  value={galleryForm.cover_image}
                  onChange={(e) => setGalleryForm({ ...galleryForm, cover_image: e.target.value })}
                  placeholder="https://example.com/cover.jpg"
                />
                {galleryForm.cover_image && (
                  <div style={{ marginTop: '10px' }}>
                    <img 
                      src={galleryForm.cover_image} 
                      alt="Cover preview" 
                      style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Category (Optional)</label>
                <input
                  type="text"
                  value={galleryForm.category}
                  onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })}
                  placeholder="e.g., Events, Community, Activities"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={galleryForm.order}
                    onChange={(e) => setGalleryForm({ ...galleryForm, order: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={galleryForm.is_active}
                      onChange={(e) => setGalleryForm({ ...galleryForm, is_active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowGalleryForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingGallery ? 'Update Gallery' : 'Create Gallery'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
        gap: '20px',
        marginTop: '20px'
      }}>
        {galleries.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
            <p>No galleries found. Create your first gallery to get started!</p>
          </div>
        ) : (
          galleries.map((gallery) => (
            <div key={gallery.id} style={{
              border: '1px solid #ddd',
              borderRadius: '12px',
              overflow: 'hidden',
              background: '#fff',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
            }}
            onClick={() => viewGallery(gallery)}
            >
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', background: '#f0f0f0' }}>
                {gallery.cover_image ? (
                  <img 
                    src={gallery.cover_image} 
                    alt={gallery.title}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#999',
                    fontSize: '48px'
                  }}>
                    <i className="fas fa-images"></i>
                  </div>
                )}
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  padding: '5px 10px',
                  borderRadius: '20px',
                  fontSize: '12px'
                }}>
                  {gallery.items?.length || 0} images
                </div>
              </div>
              <div style={{ padding: '15px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>{gallery.title}</h3>
                {gallery.description && (
                  <p style={{ 
                    margin: '0 0 10px 0', 
                    fontSize: '14px', 
                    color: '#666',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {gallery.description}
                  </p>
                )}
                {gallery.category && (
                  <span style={{ 
                    display: 'inline-block',
                    background: '#e9ecef',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    color: '#666',
                    marginBottom: '10px'
                  }}>
                    {gallery.category}
                  </span>
                )}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginTop: '15px'
                }}>
                  <span style={{ 
                    fontSize: '12px', 
                    color: '#999',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <i className={`fas fa-${gallery.is_active ? 'check-circle' : 'times-circle'}`} 
                       style={{ color: gallery.is_active ? '#28a745' : '#dc3545' }}></i>
                    {gallery.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <div style={{ display: 'flex', gap: '5px' }} onClick={(e) => e.stopPropagation()}>
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditGallery(gallery);
                      }}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button 
                      className="btn btn-sm btn-warning"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleActive(gallery);
                      }}
                    >
                      <i className={`fas fa-${gallery.is_active ? 'eye-slash' : 'eye'}`}></i>
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteGallery(gallery.id);
                      }}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ImageGalleryManager;

